import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LevelSkeleton } from '../../components/shared/Level';
import Filters from './components/Filters';
import SortMenu from './components/SortMenu';
import { getLevels } from './api/getLevels';
import { useQuery } from '@tanstack/react-query';
import useSessionStorage from '../../hooks/useSessionStorage';
import {
    BooleanParam,
    createEnumParam,
    NumberParam,
    StringParam,
    useQueryParam,
    useQueryParams,
    withDefault,
} from 'use-query-params';
import { QueryParamNames } from './enums/QueryParamNames';
import { Heading1, Heading2 } from '../../components/headings/index';
import Page from '../../components/layout/Page';
import { useNavigate } from 'react-router';
import SearchInput from '../../components/input/search/Search';
import pluralS from '../../utils/pluralS';
import IconButton from '../../components/input/buttons/icon/IconButton';
import { useShortcut } from 'react-keybind';
import { useApp } from '../../context/app/useApp';
import { LevelViewType } from '../../context/app/AppContext';
import PageButtons from '../../components/shared/PageButtons';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { UserCard } from './components/UserCard';
import { useSearchUsers } from './hooks/useSearchUsers';
import { RangeFilterLabel } from './components/RangeFilterLabel';
import { FilterLabel } from './components/FilterLabel';
import { GridLevelPresenter, ListLevelPresenter } from './components/LevelPresenter';
import LevelViewToggle from '../../components/shared/LevelViewToggle';
import { SortDirection, type Difficulty } from '@gdladder/shared/enums';
import { LevelSortType } from '@gdladder/shared/features';
import { FuseZone } from '../../components/FuseZone';
import { useDebounceValue } from 'usehooks-ts';

interface SavedFilters {
    [QueryParamNames.NAME]?: string;
    [QueryParamNames.MAX_RATING]?: number;
    [QueryParamNames.MIN_RATING]?: number;
    [QueryParamNames.MAX_ENJOYMENT]?: number;
    [QueryParamNames.MIN_ENJOYMENT]?: number;
    [QueryParamNames.LENGTH]: keyof typeof lengths;
    [QueryParamNames.DIFFICULTY]: Difficulty | undefined;
    [QueryParamNames.MIN_SUBMISSION_COUNT]?: number;
    [QueryParamNames.MAX_SUBMISSION_COUNT]?: number;
    [QueryParamNames.MIN_ENJOYMENT_COUNT]?: number;
    [QueryParamNames.MAX_ENJOYMENT_COUNT]?: number;
}

type NumericFilterKeys =
    | QueryParamNames.MIN_RATING
    | QueryParamNames.MAX_RATING
    | QueryParamNames.MIN_ENJOYMENT
    | QueryParamNames.MAX_ENJOYMENT
    | QueryParamNames.MIN_SUBMISSION_COUNT
    | QueryParamNames.MAX_SUBMISSION_COUNT
    | QueryParamNames.MIN_ENJOYMENT_COUNT
    | QueryParamNames.MAX_ENJOYMENT_COUNT;

const lengths = {
    1: 'tiny',
    2: 'short',
    3: 'medium',
    4: 'long',
    5: 'xl',
    6: 'platformer',
};

export function SearchPage() {
    const [savedFilters, setSavedFilters] = useSessionStorage<Partial<SavedFilters>>('level-filters', {});
    const [showFilters, setShowFilters] = useSessionStorage('show-filters', false);
    const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
    const app = useApp();

    const [queryParams, setQueryParams] = useQueryParams({
        [QueryParamNames.NAME]: StringParam,
        [QueryParamNames.CREATOR]: StringParam,
        [QueryParamNames.SONG]: StringParam,
        [QueryParamNames.SORT]: withDefault(createEnumParam(Object.values(LevelSortType)), LevelSortType.LEVEL_ID),
        [QueryParamNames.SORT_DIRECTION]: withDefault(createEnumParam(Object.values(SortDirection)), SortDirection.ASC),
        [QueryParamNames.MIN_RATING]: NumberParam,
        [QueryParamNames.MAX_RATING]: NumberParam,
        [QueryParamNames.MIN_ENJOYMENT]: NumberParam,
        [QueryParamNames.MAX_ENJOYMENT]: NumberParam,
        [QueryParamNames.DIFFICULTY]: StringParam,
        [QueryParamNames.LENGTH]: NumberParam,
        [QueryParamNames.MIN_DURATION]: NumberParam,
        [QueryParamNames.MAX_DURATION]: NumberParam,
        [QueryParamNames.MIN_OBJECT_COUNT]: NumberParam,
        [QueryParamNames.MAX_OBJECT_COUNT]: NumberParam,
        [QueryParamNames.MIN_DENSITY]: NumberParam,
        [QueryParamNames.MAX_DENSITY]: NumberParam,
        [QueryParamNames.MIN_SUBMISSION_COUNT]: NumberParam,
        [QueryParamNames.MAX_SUBMISSION_COUNT]: NumberParam,
        [QueryParamNames.MIN_ENJOYMENT_COUNT]: NumberParam,
        [QueryParamNames.MAX_ENJOYMENT_COUNT]: NumberParam,
        [QueryParamNames.MIN_DEVIATION]: NumberParam,
        [QueryParamNames.MAX_DEVIATION]: NumberParam,
        [QueryParamNames.MIN_ID]: NumberParam,
        [QueryParamNames.MAX_ID]: NumberParam,
        [QueryParamNames.TWO_PLAYER]: StringParam,
        [QueryParamNames.UPDATE]: StringParam,
        [QueryParamNames.TOP_TAG_ID]: StringParam,
        [QueryParamNames.EXCLUDE_COMPLETED]: BooleanParam,
        [QueryParamNames.EXCLUDE_UNRATED]: BooleanParam,
        [QueryParamNames.EXCLUDE_UNRATED_ENJOYMENT]: BooleanParam,
        [QueryParamNames.EXCLUDE_RATED]: BooleanParam,
        [QueryParamNames.EXCLUDE_RATED_ENJOYMENT]: BooleanParam,
        [QueryParamNames.IN_PACK]: BooleanParam,
    });

    useEffect(() => {
        setQueryParams(savedFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { registerShortcut, unregisterShortcut } = useShortcut()!;
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const scrollToSearchInput = useCallback(() => {
        if (searchInputRef.current) {
            searchInputRef.current.scrollIntoView({ behavior: 'instant', block: 'center' });
            setTimeout(() => {
                searchInputRef.current?.focus();
                searchInputRef.current?.select();
            }, 100);
        }
    }, []);

    // Register the shortcut to focus the search input
    useEffect(() => {
        const keybinds = ['ctrl+k', 'cmd+k'];
        registerShortcut(scrollToSearchInput, keybinds, 'Search', 'Search levels');
        return () => {
            unregisterShortcut(keybinds);
        };
    }, [registerShortcut, scrollToSearchInput, unregisterShortcut]);

    const onSearch = useCallback(() => {
        setSavedFilters(queryParams as typeof savedFilters);
        setShowFilters(false);
    }, [queryParams, setSavedFilters, setShowFilters]);

    function reset() {
        setSavedFilters({});
        setQueryParams({}, 'replace');
        setPage(0);
    }

    const { status: levelSearchStatus, data: levelSearchData } = useQuery({
        queryKey: [
            'searchLevels',
            {
                ...savedFilters,
                // difficulty: queryParams[QueryParamNames.DIFFICULTY]
                //     ? queryParams[QueryParamNames.DIFFICULTY]
                //     : undefined,
                sort: queryParams[QueryParamNames.SORT],
                sortDirection: queryParams[QueryParamNames.SORT_DIRECTION],
                page,
            },
        ],
        queryFn: () =>
            getLevels({
                ...savedFilters,
                // difficulty: queryParams[QueryParamNames.DIFFICULTY]
                //     ? queryParams[QueryParamNames.DIFFICULTY] as Difficulty
                //     : undefined,
                sort: queryParams[QueryParamNames.SORT],
                sortDirection: queryParams[QueryParamNames.SORT_DIRECTION],
                page,
            }),
    });

    useEffect(() => {
        if (levelSearchData && levelSearchData.total > 0 && levelSearchData.data.length === 0) {
            setPage(0);
        }
    }, [levelSearchData, setPage]);

    const [userPage, setUserPage] = useSessionStorage('user-search-page', 0);
    const [username, setUsername] = useDebounceValue(savedFilters[QueryParamNames.NAME], 300);
    const searchUserOptions = useMemo(() => ({ name: username, limit: 16, page: userPage }), [username, userPage]);
    const { status: userSearchStatus, data: userSearchData } = useSearchUsers(searchUserOptions);

    const onNameChange = useCallback(
        (newName: string) => {
            setQueryParams({
                ...queryParams,
                [QueryParamNames.NAME]: newName || undefined,
            });
        },
        [queryParams, setQueryParams],
    );

    // Up and down arrow key navigation
    const [selection, setSelection] = useState(0);
    const navigate = useNavigate();
    function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelection((prev) => Math.min(prev + 1, levelSearchData?.data.length ?? 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelection((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            if (selection > 0) {
                const level = levelSearchData?.data[selection - 1];
                if (level) {
                    void navigate('/level/' + level.id);
                }
            } else {
                void onSearch();
            }
        }
    }

    const filterLabels: ReactNode[] = useMemo(() => {
        const labels: ReactNode[] = [];

        const removeFilters = (updates: Partial<SavedFilters>) => {
            setQueryParams({ ...queryParams, ...updates });
            setSavedFilters((prev) => ({ ...prev, ...updates }));
        };

        const addRangeFilterLabel = (minKey: NumericFilterKeys, maxKey: NumericFilterKeys, label: string) => {
            const minValue = minKey !== undefined ? savedFilters[minKey] : undefined;
            const maxValue = maxKey !== undefined ? savedFilters[maxKey] : undefined;

            if (minValue !== undefined || maxValue !== undefined) {
                labels.push(
                    <RangeFilterLabel
                        min={minValue}
                        max={maxValue}
                        label={label}
                        onRemove={() => removeFilters({ [minKey]: undefined, [maxKey]: undefined })}
                    />,
                );
            }
        };

        const addStringFilterLabel = (filter: string, value: string, paramKey: keyof SavedFilters) => {
            if (!value) return;
            labels.push(
                <FilterLabel label={`${filter}: ${value}`} onRemove={() => removeFilters({ [paramKey]: undefined })} />,
            );
        };

        addRangeFilterLabel(QueryParamNames.MIN_RATING, QueryParamNames.MAX_RATING, 'tier');
        addRangeFilterLabel(QueryParamNames.MIN_ENJOYMENT, QueryParamNames.MAX_ENJOYMENT, 'enjoyment');
        addRangeFilterLabel(QueryParamNames.MIN_SUBMISSION_COUNT, QueryParamNames.MAX_SUBMISSION_COUNT, 'submissions');
        addRangeFilterLabel(
            QueryParamNames.MIN_ENJOYMENT_COUNT,
            QueryParamNames.MAX_ENJOYMENT_COUNT,
            'enjoyment ratings',
        );

        if (savedFilters[QueryParamNames.DIFFICULTY])
            addStringFilterLabel('difficulty', savedFilters[QueryParamNames.DIFFICULTY], QueryParamNames.DIFFICULTY);
        if (savedFilters[QueryParamNames.LENGTH])
            addStringFilterLabel('length', lengths[savedFilters[QueryParamNames.LENGTH]], QueryParamNames.LENGTH);

        return labels;
    }, [queryParams, savedFilters, setQueryParams, setSavedFilters]);

    return (
        <Page title='Search'>
            <Heading1>Levels</Heading1>
            <div className='flex gap-2 items-center transition-all'>
                <SearchInput
                    ref={searchInputRef}
                    onKeyDown={onKeyDown}
                    value={queryParams[QueryParamNames.NAME] ?? ''}
                    onChange={(e) => onNameChange(e.target.value.trimStart().slice(0, 22))}
                    onClear={() => onNameChange('')}
                    onMenu={() => setShowFilters((prev) => !prev)}
                    autoFocus
                    placeholder='Search by name or ID'
                />
                <IconButton color='filled' onClick={() => void onSearch()}>
                    <i className='bx bx-search' />
                </IconButton>
            </div>
            <Filters reset={reset} show={showFilters} />
            <div className='flex justify-between'>
                <SortMenu />
                <LevelViewToggle className='self-end' />
            </div>
            {levelSearchStatus === 'error' && (
                <Heading2 className='text-center'>An error occurred while searching</Heading2>
            )}
            {levelSearchStatus === 'pending' && (
                <div className='my-4 space-y-2'>
                    {Array.from({ length: 12 }, (_, i) => (
                        <LevelSkeleton key={i} />
                    ))}
                </div>
            )}
            {levelSearchStatus === 'success' && (
                <>
                    {filterLabels.length > 0 && (
                        <div className='py-2'>
                            <p>
                                <b className='text-lg'>Filters:</b>
                            </p>
                            <ul className='flex flex-wrap gap-2'>
                                {filterLabels.map((filter, i) => (
                                    <li key={i}>{filter}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {levelSearchData.data && app.levelViewType === LevelViewType.LIST ? (
                        <ListLevelPresenter levels={levelSearchData.data} selectedLevel={selection} />
                    ) : (
                        <GridLevelPresenter levels={levelSearchData.data} selectedLevel={selection} />
                    )}
                    <div className='my-4'>
                        <PageButtons
                            limit={levelSearchData.limit}
                            total={levelSearchData.total}
                            page={levelSearchData.page}
                            onPageChange={setPage}
                        />
                        <p className='text-center'>
                            <b>{levelSearchData.total}</b> level{pluralS(levelSearchData.total)} found
                        </p>
                    </div>
                </>
            )}
            <FuseZone id='gddl_footer' />
            <section>
                <Heading1>Users</Heading1>
                <div className='mb-4'>
                    <SearchInput
                        value={username}
                        onChange={(e) => setUsername(e.target.value.trimStart().slice(0, 32))}
                        onClear={() => setUsername('')}
                        placeholder='Search user'
                    />
                </div>
                {userSearchStatus === 'pending' && <LoadingSpinner />}
                {userSearchStatus === 'success' && (
                    <>
                        <ul className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mb-4'>
                            {userSearchData.data.map((user) => (
                                <UserCard key={user.ID} user={user} />
                            ))}
                        </ul>
                        <PageButtons
                            limit={userSearchData.limit}
                            total={userSearchData.total}
                            page={userSearchData.page}
                            onPageChange={setUserPage}
                        />
                        <p className='text-center'>
                            <b>{userSearchData.total}</b> user{pluralS(userSearchData.total)} found
                        </p>
                    </>
                )}
            </section>
        </Page>
    );
}
